import React, {FC} from "react"

import {H2, H4, H6, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import theme from "@irati/ui/src/global/theme"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {RenewalOption} from "@irati/types"
import {CurrencyType} from "@irati/payments/enums"

import {GradientBody, Recommended, StyledCard, Height, OuterCircle, InnerCircle} from "./styled"
interface Props extends InjectIntl {
  quote: RenewalOption
  oneYearQuote?: RenewalOption
  isRecommended: boolean
  onUpdate: (q: RenewalOption) => void
  hideSelector: boolean
  isSelected: boolean
  invertColors?: boolean
  currencyCode: CurrencyType
}

const RenewalCard: FC<Props> = ({
  quote,
  oneYearQuote,
  isRecommended,
  onUpdate,
  hideSelector,
  isSelected,
  invertColors,
  currencyCode = CurrencyType.GBP,
  formatMessage
}) => {
  const {termLength, costPerSeatMonth, annualCost, discount} = quote
  const textColor = isSelected
    ? invertColors
      ? theme.colors.greys.white
      : theme.colors.primary2
    : invertColors
    ? theme.colors.primary2
    : theme.colors.greys.white
  return (
    <StyledCard
      invertColors={invertColors}
      hideSelector={hideSelector}
      isSelected={isSelected}
      bgColor={
        isSelected
          ? invertColors
            ? theme.colors.primary2
            : theme.colors.greys.white
          : invertColors
          ? theme.colors.greys.white
          : theme.colors.primary2
      }
      padding={2}
      onClick={() => onUpdate && onUpdate(quote)}>
      <Flex>
        {!hideSelector && (
          <OuterCircle isSelected={isSelected} invertColors={invertColors}>
            <InnerCircle isSelected={isSelected} invertColors={invertColors} />
          </OuterCircle>
        )}
        <H4 marginBottom={1} marginLeft={1} color={textColor}>{`${termLength} ${formatMessage({
          id: "admin.billing.renewals.renewalCard.title"
        })}`}</H4>
      </Flex>
      <Height>
        {!!discount && (
          <Flex marginTop={0.5}>
            <GradientBody invertColors={invertColors} isSelected={isSelected} small padding={0.5}>{`${
              Math.round(discount * 10) / 10
            }${formatMessage({
              id: "admin.billing.renewals.renewalCard.discount"
            })}`}</GradientBody>
          </Flex>
        )}
      </Height>
      <Flex direction="row" justifyContent="flex-end" alignItems="center">
        {oneYearQuote && (
          <H4 textDecoration="line-through" marginRight={1} color={textColor}>
            {formatCurrency({currencyCode, value: oneYearQuote.annualCost})}
          </H4>
        )}
        <H2 color={textColor}>{formatCurrency({currencyCode, value: annualCost})}</H2>
      </Flex>
      <Flex justifyContent="flex-end" marginBottom={1}>
        <Label small color={theme.colors.greys.medium}>
          <FormattedMessage id="admin.billing.renewals.renewalCard.cost" />
        </Label>
      </Flex>
      {isRecommended && (
        <Recommended small padding={0.5}>
          <FormattedMessage id="admin.billing.renewals.renewalCard.recommended" />
        </Recommended>
      )}
      <Flex justifyContent="flex-end">
        {oneYearQuote && (
          <H6 textDecoration="line-through" small marginRight={1} color={textColor}>
            {formatCurrency({currencyCode, value: oneYearQuote.costPerSeatMonth})}
          </H6>
        )}
        <H6 color={textColor}>{`${formatCurrency({
          currencyCode,
          value: costPerSeatMonth
        })} ${formatMessage({id: "admin.billing.renewals.renewalCard.costPerMonth"})}`}</H6>
      </Flex>
    </StyledCard>
  )
}

export default injectIntl(RenewalCard)
